import React, { useEffect, useMemo, useState } from 'react';
import pt from 'prop-types';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useIndex, usePrevious } from './hooks';
import { calculateAdditionalPoses } from './functions';
import PreviewArea from './PreviewArea';
import styles from './MultiPose.module.scss';
import Carousel from './Carousel';
import NavBar from './NavBar';
import routes from '../../../routes';
import useImageOrientation from '../../../hooks/useImageOrientation';
import { IMAGE_ORIENTATION_CACHE } from '../../../utils/variables';
import {
  getAppropriateBg, getAppropriateForeground,
} from '../../_pages/_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import ProgressSheets from './ProgressSheets';
import useForegrounds from '../../../hooks/useForegrounds';
import usePricelist from '../../../hooks/usePricelist';
import usePrintTemplate from '../../../hooks/usePrintTemplate';
import { useCustomer } from '../../../contexts/CustomerContext';
import useStudentForegrounds from "../../../hooks/useStudentForegrounds";
import useQueryString from "../../../hooks/use-query-string";

export default function Builder({
  isEditingSingleSheet,
  backgrounds,
  favoriteIds,
  imageTypes,
  initialSheets,
  option,
  packageId,
  packagePoses = [],
  poses,
  onConfirm,
  inReview = false,
}) {
  const { customer } = useCustomer();
  const { type } = useQueryString();
  // Data state
  const [additionalPoses, setAdditionalPoses] = useState(0);
  const previousAdditionalPoses = usePrevious(additionalPoses);
  const { data: printTemplates } = usePrintTemplate();
  const [unconfirmedPose, setUnconfirmedPose] = useState(null);
  const [foregrounds, setForegrounds] = useState([])
  const { data: pricelist } = usePricelist(packageId);
  // The student's senior season for THIS order, taken from the ordered poses'
  // folder (e.g. "seniors2027") -- the authoritative per-student class year. We
  // scope the overlay carousel to it so the class of 2026 and 2027 are offered
  // their own "Class of YYYY" set and old-year orders keep theirs. Falls back to
  // the package season, then any pose folder; undefined while poses load (then
  // the carousel shows all senior overlays until the season is known).
  const seniorSeason = useMemo(() => {
    const folders = (poses || []).map((p) => p.folder).filter(Boolean);
    return folders.find((f) => String(f).toLowerCase().startsWith('seniors'))
      || pricelist?.season
      || folders[0];
  }, [poses, pricelist?.season]);
  const { data: initialForegrounds } = useForegrounds(seniorSeason);
  const { data: studentForegrounds } = useStudentForegrounds(customer?.currentStudent?.id, pricelist?.season);

  // UI State
  const [sheets, setSheets] = useState([]);

  // const [isPoseModalOpen, setIsPoseModalOpen] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState('poses');
  const [sortedPoses, setSortedPoses] = useState([]);
  const {
    decrement,
    decrementDisabled,
    increment,
    incrementDisabled,
    index: currentSheetIndex,
    setIndex,
  } = useIndex({ maxIndex: sheets.length - 1 });

  const incrementHandler = () => {
    const subset = sheets
      .map((sheet) => ({ ...sheet }))
      .slice(0, currentSheetIndex + 1);

    const numUniquePoses = [...new Set([
      ...subset.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose').map((sheet) => sheet.pose),
    ].map((pose) => pose.id))].length;

    setAdditionalPoses(Math.max(numUniquePoses - option.poses, 0));
    setSelectedIcon('poses')

    increment();
  };

  const decrementHandler = () => {
    const subset = sheets
      .map((sheet) => ({ ...sheet }))
      .slice(0, currentSheetIndex + 1);

    const numUniquePoses = [...new Set([
      ...subset.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose').map((sheet) => sheet.pose),
    ].map((pose) => pose.id))].length;

    setAdditionalPoses(Math.max(numUniquePoses - option.poses, 0));

    decrement();
  };

  const { orientation: poseOrientation } = useImageOrientation(sheets[currentSheetIndex]?.pose?.watermarkUrl || sheets[currentSheetIndex]?.pose?.imageUrl);
  const { orientation: bgOrientation } = useImageOrientation(sheets[currentSheetIndex]?.background?.imageUrl);

  useDeepCompareEffect(() => {
    const sorted = poses
      .sort((pose1, pose2) => favoriteIds.includes(pose2.id) - favoriteIds.includes(pose1.id));

    setSortedPoses(() => sorted);
  }, [poses, favoriteIds]);

  useEffect(() => {
    if (!poseOrientation) return;
    if (!bgOrientation) return;
    if (!sheets[currentSheetIndex]) return;
    if (poseOrientation === bgOrientation) return;

    const correctlyOrientatedBg = backgrounds.find((bg) => {
      const cachedData = IMAGE_ORIENTATION_CACHE[bg.imageUrl];

      return poseOrientation === cachedData?.orientation;
    });

    if (!correctlyOrientatedBg) return;

    sheets[currentSheetIndex].backgroundId = correctlyOrientatedBg.id;
    sheets[currentSheetIndex].background = correctlyOrientatedBg;

    setSheets((prev) => ([...prev]));
  }, [poseOrientation, bgOrientation, currentSheetIndex]);

  // Handlers
  const setPose = (newPose) => {
    if (sheets.length === 0) return;

    const currentSheet = sheets[currentSheetIndex];

    const currentSheetDup = { ...currentSheet };

    currentSheetDup.pose = newPose;
    currentSheetDup.poseId = newPose.id;

    if (packagePoses.length > 0) {
      let packagePose;

      if (pricelist?.multipleSeasons?.length) {
        // Case-insensitive season match — prod data has mixed casing
        // (e.g. "Spring2026" in package_poses.season vs "spring2026" in
        // pose.folder), and a strict === would silently mismatch.
        const targetFolder = (newPose.folder || '').toLowerCase();
        packagePose = packagePoses.find((packagePose) => (
          packagePose.poseNumber === newPose.number &&
          (packagePose.season || '').toLowerCase() === targetFolder
        ));
      }
      else {
        packagePose = packagePoses.find((packagePose) => packagePose.poseNumber === newPose.number);
      }

      if (packagePose) {
        currentSheetDup.background = packagePose.background;
        currentSheetDup.backgroundId = packagePose.background?.id;

        currentSheetDup.foregroundId = packagePose.foreground?.id;
        currentSheetDup.foreground = packagePose.foreground;
      } else {
        currentSheetDup.foregroundId = undefined;
        currentSheetDup.foreground = undefined;
      }
    }

    // only look at the sheets the user has currently edited when factoring in additional poses
    const subset = sheets
      .map((sheet) => ({ ...sheet }))
      .slice(0, currentSheetIndex + 1);
    //
    // if (subset[currentSheetIndex]) subset[currentSheetIndex] = currentSheetDup;
    //
    // const numUniquePoses = [...new Set([
    //   ...subset.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose').map((sheet) => sheet.pose),
    // ].map((pose) => pose.id))].length;

    // If ordering more poses than the pricelist allows, open the confirmation modal to add more poses.
    // if (!option.digitalImagesOnly && numUniquePoses > additionalPoses + option.poses) {
    //   setUnconfirmedPose(newPose);
    //   setIsPoseModalOpen(true);
    //   return;
    // }

    if (!currentSheet) return;

    sheets[currentSheetIndex].pose = newPose;
    sheets[currentSheetIndex].poseId = newPose.id;

    // If a customer changes the default pose to a new pose, they can automatically be at the maximum number
    // of new sheets and be unaware of that. Change the pose for unseen sheets in that scenario
    const unseenSheets = sheets
      .slice(currentSheetIndex + 1);

    const subsetAdditionalPoses = calculateAdditionalPoses(subset, option);
    const totalAdditionalPoses = calculateAdditionalPoses(sheets, option);

    const currentlyUsedPoseIds = subset.map((sheet) => sheet.poseId);

    if (totalAdditionalPoses > subsetAdditionalPoses) {
      unseenSheets.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose').forEach((sheet) => {
        if (currentlyUsedPoseIds.includes(sheet.poseId)) return;

        sheet.pose = subset[currentSheetIndex].pose;
        sheet.poseId = subset[currentSheetIndex].poseId;
      });
    }
    // END

    subset[currentSheetIndex].pose = newPose;
    subset[currentSheetIndex].poseId = newPose.id;

    setSheets(() => ([...subset, ...unseenSheets]));
    setAdditionalPoses(() => calculateAdditionalPoses(subset, option));
  };

  const setBackground = (newBackground) => {
    if (sheets.length === 0) return;

    const currentSheet = sheets[currentSheetIndex];

    if (!currentSheet) return;

    currentSheet.background = newBackground;
    currentSheet.backgroundId = newBackground.id;


    setSheets((prev) => ([...prev]));
  };

  const setForeground = (newForeground) => {
    if (sheets.length === 0) return;

    const currentSheet = sheets[currentSheetIndex];

    if (!currentSheet) return;

    if (newForeground.id === 99999) {
      currentSheet.foreground = undefined;
      currentSheet.foregroundId = undefined;
    } else {
      currentSheet.foreground = newForeground;
      currentSheet.foregroundId = newForeground.id;
    }

    setSheets((prev) => ([...prev]));
  };

  const onExtraPoseConfirm = () => setAdditionalPoses((prev) => prev + 1);

  // If a user confirms a pose, then add it and remove it from the unconfirmed pose
  useEffect(() => {
    if (additionalPoses === previousAdditionalPoses) return;
    if (!unconfirmedPose) return;

    setPose(unconfirmedPose);
    setUnconfirmedPose(null);
  }, [additionalPoses, previousAdditionalPoses, unconfirmedPose]);

  useEffect(() => {
    if (!initialForegrounds || !studentForegrounds) return;

    const clearForeground = { id: 99999 };

    if (studentForegrounds?.length > 0) {
      setForegrounds([clearForeground, ...studentForegrounds]);
    } else {
      setForegrounds([clearForeground, ...initialForegrounds]);
    }

  }, [initialForegrounds, studentForegrounds])

  // Initialize the sheets with placeholder data
  useEffect(() => {
    if (sortedPoses.length === 0) return;

    const firstPose = sortedPoses[0];

    let unindexedSheets;

    // For buy all / digital images only options, create one sheet per pose (not per image type)
    if (option.digitalImagesOnly && sortedPoses.length > 1) {
      console.log('BUY ALL: Creating sheets for', sortedPoses.length, 'poses');
      console.log('Digital Images Only:', option.digitalImagesOnly);
      console.log('Image Types:', imageTypes.length);

      // Get first non-yearbook image type for the buy all sheets
      const firstImageType = imageTypes.find(it => it.name !== 'Yearbook Pose')
        || imageTypes.find(it => it.name.includes('Download'))
        || imageTypes.find(it => it.name.includes('Digital'))
        || imageTypes[0];

      // Get yearbook image type for the yearbook sheet
      const yearbookImageType = imageTypes.find(it => it.name === 'Yearbook Pose');

      // Create one sheet per pose
      const poseSheets = sortedPoses.map((pose) => {
        const bg = getAppropriateBg(packagePoses, pose, backgrounds);
        const foreground = getAppropriateForeground(packagePoses, pose);

        return {
          imageType: firstImageType,
          imageTypeId: firstImageType.id,
          pose,
          poseId: pose.id,
          background: bg,
          backgroundId: bg?.id,
          foregroundId: foreground?.id,
          foreground,
        };
      });

      // Add yearbook sheet if yearbook image type exists
      if (yearbookImageType) {
        const yearbookBg = getAppropriateBg(packagePoses, firstPose, backgrounds);
        const yearbookForeground = getAppropriateForeground(packagePoses, firstPose);

        poseSheets.push({
          imageType: yearbookImageType,
          imageTypeId: yearbookImageType.id,
          pose: firstPose,
          poseId: firstPose.id,
          background: yearbookBg,
          backgroundId: yearbookBg?.id,
          foregroundId: yearbookForeground?.id,
          foreground: yearbookForeground,
        });
      }

      unindexedSheets = poseSheets;
    } else {
      // Normal flow: create sheets based on image types
      unindexedSheets = imageTypes.map((imageType) => {
        const bg = getAppropriateBg(packagePoses, firstPose, backgrounds);
        const foreground = getAppropriateForeground(packagePoses, firstPose);

        return new Array(imageType.count).fill(null).map(() => ({
          imageType,
          imageTypeId: imageType.id,
          pose: firstPose,
          poseId: firstPose.id,
          background: bg,
          backgroundId: bg?.id,
          foregroundId: foreground?.id,
          foreground,
        }));
      }).flat();
    }

    const nextSheets = unindexedSheets.map((sheet, i) => ({
      ...sheet,
      index: i,
    }))
      .map((sheet) => {
        if (sheet.imageType.requiresBg) return sheet;

        // Holiday items require PNG versions of images to not have backgrounds to display properly on the item
        return ({ ...sheet, background: undefined, backgroundId: undefined });
      });

    // If initial sheets are provided, then we will initialize the new sheets with the pose and
    // background information of the initial sheets. Since a user can select a new option, there may
    // be a mismatch in the imageType when selecting a new option or the number of sheets available.
    if (initialSheets.length > 0) {
      initialSheets.forEach((sheet, i) => {
        const index = nextSheets[i] ? i : 0;

        if (nextSheets[index]) {
          nextSheets[index].pose = sheet.pose;
          nextSheets[index].poseId = sheet.poseId.toString();
          nextSheets[index].background = sheet.background;
          nextSheets[index].backgroundId = sheet.backgroundId?.toString();
          nextSheets[index].foreground = sheet.foreground;
          nextSheets[index].foregroundId = sheet.foregroundId?.toString();
          nextSheets[index].imageType = sheet.imageType;
          nextSheets[index].imageTypeId = sheet.imageTypeId;
        }
      });
    }

    if (isEditingSingleSheet) {
      setSheets(() => [nextSheets[0]]);
    } else {
      console.log('BUY ALL: Setting sheets, total count:', nextSheets.length);
      setSheets(() => nextSheets);
    }
  }, [sortedPoses.length, initialSheets.length, imageTypes.length]);

  if (sheets.length === 0) return null;

  const filterJpgPoses = () => {
    if (!sheets[currentSheetIndex]?.imageType?.component) return false;

    return !Object.values(sheets[currentSheetIndex].imageType.component.htmlElements).some((htmlElement) => {
      if (!htmlElement.data.src) return false;

      if (htmlElement.data.src.includes('background')) return true;
    });
  };

  const handleContinue = () => {
    if (selectedIcon === 'poses') {
      setSelectedIcon('backgrounds')
    }
    else if (selectedIcon === 'backgrounds') {
        setSelectedIcon('foregrounds')
    }
    else if (selectedIcon === 'foregrounds') {
      incrementHandler()
    }
  }

  const canSelectForegrounds = sheets[currentSheetIndex]
    && sheets[currentSheetIndex].pose.isPNG()
    && sheets[currentSheetIndex].imageType.name !== 'Yearbook Pose';

  const canSelectBackgrounds = canSelectForegrounds && !filterJpgPoses();

  const isAllJpg = poses.reduce((acc, cur) => acc && !cur.extension.toLowerCase().includes('png'), true);

  const renderLayout = () => (
    <>
      <div className={styles.carousels}>
        {!sortedPoses.some(pose => pose.url.includes('PLACEHOLDER')) && (
          <Carousel
            filterJpgPoses={filterJpgPoses()}
            items={sortedPoses}
            onClick={(pose) => setPose(pose)}
            text="POSE"
            favoriteIds={favoriteIds}
          />
        )}
        {!isAllJpg && (
          <div style={{ position: 'relative' }}>
            <div style={{ visibility: canSelectBackgrounds ? 'initial' : 'hidden' }}>
              <Carousel
                items={backgrounds}
                onClick={(background) => setBackground(background)}
                useOrientation={poseOrientation}
                text="BACKGROUND"
              />
            </div>
            {!canSelectBackgrounds && (
              <p className={styles.jpgPoseSelected}>The background cannot change on the pose you have selected.</p>
            )}
          </div>
        )}
        <div className={styles.btnContainers}>
          {!inReview && (
            <button disabled={decrementDisabled} onClick={decrementHandler} type="button">Back</button>
          )}
          {(!incrementDisabled || inReview) && (
            <button onClick={inReview ? () => onConfirm(sheets, 0) : incrementHandler} type="button">
              Confirm
            </button>
          )}
          {incrementDisabled && packageId && (
            <button onClick={() => onConfirm(sheets, additionalPoses)} type="button">
              {sheets.length === 1 ? 'Add to Cart' : 'Add All to Cart'}
            </button>
          )}
        </div>
      </div>
    </>
  )

  const renderNewLayout = () => (
    <>
      <div className={styles.carousels}>
        {selectedIcon === 'poses' && (
          <Carousel
            filterJpgPoses={filterJpgPoses()}
            items={sortedPoses}
            onClick={(pose) => setPose(pose)}
            favoriteIds={favoriteIds}
          />
        )}
        {!isAllJpg && selectedIcon === 'backgrounds' && (
          <div style={{ position: 'relative' }}>
            <div style={{ visibility: canSelectBackgrounds ? 'initial' : 'hidden' }}>
              <Carousel
                items={backgrounds}
                onClick={(background) => setBackground(background)}
                useOrientation={poseOrientation}
              />
            </div>
            {!canSelectBackgrounds && (
              <p className={styles.jpgPoseSelected}>The background cannot change on the pose you have selected.</p>
            )}
          </div>
        )}
        {!isAllJpg && selectedIcon === 'foregrounds' && (
          <div style={{ position: 'relative' }}>
            <div style={{ visibility: canSelectForegrounds ? 'initial' : 'hidden' }}>
              <Carousel
                items={foregrounds}
                onClick={(foreground) => setForeground(foreground)}
                useOrientation={poseOrientation}
                background={backgrounds.find(background => background.color.includes('Black'))}
              />
            </div>
            {!canSelectForegrounds && (
              <p className={styles.jpgPoseSelected}>The foreground cannot change on the pose you have selected.</p>
            )}
          </div>
        )}
        <NavBar selectedIcon={selectedIcon} setSelectedIcon={setSelectedIcon} />

        <div className={styles.btnContainers}>
          {!inReview && (
            <button disabled={decrementDisabled} onClick={decrementHandler} type="button">Back</button>
          )}
          {(!incrementDisabled || inReview) && (
            <button onClick={inReview ? () => onConfirm(sheets, 0) : handleContinue} type="button">
              Confirm
            </button>
          )}
          {incrementDisabled && packageId && (
            <button onClick={() => onConfirm(sheets, additionalPoses)} type="button">
              {sheets.length === 1 ? 'Add to Cart' : 'Add All to Cart'}
            </button>
          )}
        </div>
      </div>
    </>
  )

  return (
    <>
      <div className={styles.builder}>
        {/*<ExtraPoseConfirmModal*/}
        {/*  additionalPoses={calculateAdditionalPoses(sheets, option)}*/}
        {/*  isOpen={isPoseModalOpen}*/}
        {/*  option={option}*/}
        {/*  setIsOpen={setIsPoseModalOpen}*/}
        {/*  onConfirm={onExtraPoseConfirm}*/}
        {/*/>*/}
        <PreviewArea
          setSheets={setSheets}
          currentSheetIndex={currentSheetIndex}
          sheet={sheets[currentSheetIndex]}
          imageTypes={imageTypes}
          sheets={sheets}
          type={type}
          printTemplates={printTemplates}
        />

        {pricelist?.isSeniorPortraits ? renderNewLayout() : renderLayout()}
      </div>
      <ProgressSheets sheets={sheets} index={currentSheetIndex} />
    </>
  );
}

Builder.defaultProps = {
  favoriteIds: [],
  initialSheets: [],
  nextRoute: routes.review(),
};

Builder.propTypes = {
  favoriteIds: pt.arrayOf(pt.string),
  initialSheets: pt.array,
  nextRoute: pt.string,
  option: pt.object.isRequired,
};
